import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import Checkbox from '@repo/ui/checkbox'
import { useForm } from 'react-hook-form'

import { useNativeCountryField } from '../../../../lib/hooks/use-native-country-name'
import { OlJourneyPlaceInput } from '../../../../lib/types/models/ol/ol-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import { ControlledAdressInput } from '../../../elements/inputs/address-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ValidationButtons from '../../../elements/validation-buttons'
import { OL_JOURNEY_PLACE_FORM_SCHEMA_VALIDATION } from '../utils'

type Props = BaseFormProps<OlJourneyPlaceInput, true> & {
  className?: string
}

const OlJourneyPlaceForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<OlJourneyPlaceInput>({
    resolver: zodResolver(OL_JOURNEY_PLACE_FORM_SCHEMA_VALIDATION),
    defaultValues,
    mode: FORM_MODE
  })
  useNativeCountryField(form, 'country')
  useNativeCountryField(form, 'tuitionCountry')

  const isSameAddresForTuition = form.watch('isSameAddresForTuition')

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <FormField
            control={form.control}
            name={'name'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    required
                    type={'text'}
                    label="NOM"
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'address1'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledAdressInput
                    nativeCountryNames
                    required
                    label="Adresse"
                    value={field.value}
                    onChange={field.onChange}
                    onDetailsChange={(placeDetails) => {
                      form.setValue('address1', placeDetails.address, { shouldValidate: true })
                      form.setValue('zipCode', placeDetails.postalCode, {
                        shouldValidate: true
                      })
                      form.setValue('city', placeDetails.city, { shouldValidate: true })
                      form.setValue('country', placeDetails.country, {
                        shouldValidate: true
                      })
                      form.trigger(['address1', 'zipCode', 'city', 'country'])
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'address2'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    type={'text'}
                    label="Complément d'adresse"
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name={'city'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      required
                      type={'text'}
                      label="Ville"
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'zipCode'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      required
                      type={'text'}
                      label="Code postal"
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name={'country'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    type={'text'}
                    label="Pays"
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'isSameAddresForTuition'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    label={'Même adresse pour les cours'}
                    onChange={(checked) => {
                      field.onChange(checked)
                      // Reset tuition fields
                      form.setValue('tuitionName', '')
                      form.setValue('tuitionAddress1', '')
                      form.setValue('tuitionAddress2', '')
                      form.setValue('tuitionCity', '')
                      form.setValue('tuitionZipCode', '')
                      form.setValue('tuitionCountry', '')
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {!isSameAddresForTuition && (
            <>
              <FormField
                control={form.control}
                name={'tuitionName'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        type={'text'}
                        label="NOM"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'tuitionAddress1'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledAdressInput
                        nativeCountryNames
                        label="Adresse"
                        value={field.value}
                        onChange={field.onChange}
                        onDetailsChange={(placeDetails) => {
                          form.setValue('tuitionAddress1', placeDetails.address, {
                            shouldValidate: true
                          })
                          form.setValue('tuitionZipCode', placeDetails.postalCode, {
                            shouldValidate: true
                          })
                          form.setValue('tuitionCity', placeDetails.city, { shouldValidate: true })
                          form.setValue('tuitionCountry', placeDetails.country, {
                            shouldValidate: true
                          })
                          form.trigger([
                            'tuitionAddress1',
                            'tuitionZipCode',
                            'tuitionCity',
                            'tuitionCountry'
                          ])
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'tuitionAddress2'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        type={'text'}
                        label="Complément d'adresse"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex gap-4">
                <FormField
                  control={form.control}
                  name={'tuitionCity'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput
                          type={'text'}
                          label="Ville"
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={'tuitionZipCode'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput
                          type={'text'}
                          label="Code postal"
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name={'tuitionCountry'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        type={'text'}
                        label="Pays"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </>
          )}
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default OlJourneyPlaceForm
